import AccommodationPackageInclusionsList from 'components/AccommodationKit/AccommodationPackageInclusionsList'
import AccommodationRoomDetailsListContent from 'components/AccommodationKit/AccommodationRoomDetailsListContent'
import Clickable from 'components/Common/Clickable'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import moment from 'moment'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'

const TierAccentText = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

const IMAGE_PARAMS: ImageParams = {
  mobileAspectRatio: '16:8',
  tabletAspectRatio: '16:8',
  quality: 'good',
}

const INCLUSIONS_COLLAPSED_MAX_HEIGHT = 80

interface Props {
  upgradeType: App.LoyaltyStayUpgradeType
  targetPackage: App.HotelPackage
  offer?: App.Offer | App.HotelOffer | App.VillaOffer | App.BundleOffer
  checkInDate?: string
  checkOutDate?: string
  /** Drives the tier-coloured accent on the inclusions heading. */
  tier: App.LoyaltyTier
  /** Opens the full room details modal. Room sections only — packages have no equivalent. */
  onViewRoomDetails?: () => void
}

function LoyaltyStayUpgradeSectionContent({
  upgradeType,
  targetPackage,
  offer,
  checkInDate,
  checkOutDate,
  tier,
  onViewRoomDetails,
}: Props) {
  const intl = useIntl()
  const checkIn = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOut = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(targetPackage, { checkIn, checkOut })

  const inclusionsList = <LargeContentToggle
    maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
    showMoreText={intl.formatMessage({
      id: 'jcIKjKGxYTZDye5WUFEJ',
      defaultMessage: 'View more inclusions',
    })}
    showLessText={intl.formatMessage({
      id: 'n5wlSwDhZXYnvuDemfWI',
      defaultMessage: 'View less inclusions',
    })}
  >
    <Group direction="vertical" gap={8}>
      {!!luxPlusInclusions.length && <LuxPlusInclusions
        luxPlusInclusions={luxPlusInclusions}
        accommodationOffer={offer}
        hideUpsellModal
      />}
      {!!bonusInclusions.length && <AccommodationPackageInclusionsList variant="bonus">
        {bonusInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
      </AccommodationPackageInclusionsList>}
      <AccommodationPackageInclusionsList variant={bonusInclusions.length ? 'additional' : undefined}>
        {inclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size="medium" />)}
      </AccommodationPackageInclusionsList>
    </Group>
  </LargeContentToggle>

  if (upgradeType === 'room') {
    // Shaped like the amenities-and-policies link this list renders by default, so it reads as the
    // end of the amenity run rather than a separate control below it.
    const moreRoomDetailsCta = !!onViewRoomDetails && <Clickable onClick={onViewRoomDetails}>
      <BodyTextBlock variant="medium" endIcon={<LineAngleRightIcon aria-hidden="true" />} underline>
        {intl.formatMessage({
          id: '2CSDc0cZaUGGIedvAk5E',
          defaultMessage: 'More room details',
        })}
      </BodyTextBlock>
    </Clickable>

    return <Group direction="vertical" gap={20}>
      {!!targetPackage.roomType.images.length && <ImageGallery
        images={targetPackage.roomType.images}
        imageParams={IMAGE_PARAMS}
      />}
      <AccommodationRoomDetailsListContent
        roomPackage={targetPackage}
        showHotelRoomAmenities
        amenitiesAndPoliciesCta={moreRoomDetailsCta}
      />
      {inclusionsList}
    </Group>
  }

  return <Group direction="vertical" gap={8}>
    <TierAccentText className={`tier-${tier}`} variant="medium" weight="bold">
      {intl.formatMessage({
        id: '66RRbkMfbOZmc0UUAruC',
        defaultMessage: 'Your upgraded inclusions',
      })}
    </TierAccentText>
    {inclusionsList}
  </Group>
}

export default LoyaltyStayUpgradeSectionContent
